/**
 * Terms & Conditions Service
 * Handles all terms and service agreement-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class TermsService {
  /**
   * Get current active terms and conditions
   * @returns {Promise<Object>} Response with current terms document
   */
  async getCurrentTerms() {
    try {
      const response = await axios.get(API_ENDPOINTS.TERMS.CURRENT);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get current active service agreement
   * @returns {Promise<Object>} Response with current service agreement document
   */
  async getCurrentServiceAgreement() {
    try {
      const response = await axios.get(API_ENDPOINTS.TERMS.SERVICE_AGREEMENT_CURRENT);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get terms by specific version
   * @param {string} version - Terms version (e.g., "1.0", "2.0")
   * @returns {Promise<Object>} Response with specific version of terms
   */
  async getTermsByVersion(version) {
    try {
      const response = await axios.get(API_ENDPOINTS.TERMS.VERSION(version));
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Accept terms and conditions
   * @param {string} version - Terms version being accepted
   * @returns {Promise<Object>} Response with acceptance confirmation
   */
  async acceptTerms(version) {
    try {
      const response = await axios.post(API_ENDPOINTS.TERMS.ACCEPT_TERMS, {
        version: version,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Accept service agreement
   * @param {string} version - Service agreement version being accepted
   * @returns {Promise<Object>} Response with acceptance confirmation
   */
  async acceptServiceAgreement(version) {
    try {
      const response = await axios.post(API_ENDPOINTS.TERMS.ACCEPT_AGREEMENT, {
        version: version,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Accept both terms and service agreement
   * @param {string} termsVersion - Terms version being accepted
   * @param {string} agreementVersion - Service agreement version being accepted
   * @returns {Promise<Object>} Response with both acceptance confirmations
   */
  async acceptBoth(termsVersion, agreementVersion) {
    try {
      const response = await axios.post(API_ENDPOINTS.TERMS.ACCEPT_BOTH, {
        terms_version: termsVersion,
        agreement_version: agreementVersion,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's acceptance status for terms and agreement
   * @returns {Promise<Object>} Response with acceptance status for both documents
   */
  async getAcceptanceStatus() {
    try {
      const response = await axios.get(API_ENDPOINTS.TERMS.ACCEPTANCE_STATUS);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's acceptance history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} Response with acceptance history and pagination
   */
  async getAcceptanceHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 20,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.TERMS.ACCEPTANCE_HISTORY}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if user needs to accept new terms (helper method)
   * @returns {Promise<boolean>} True if user needs to accept updated terms
   */
  async needsAcceptance() {
    try {
      const status = await this.getAcceptanceStatus();
      return !status.terms?.accepted || !status.service_agreement?.accepted;
    } catch (error) {
      // If user is not authenticated or other error, assume no acceptance needed
      return false;
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage =
        error.response.data.error ||
        error.response.data.message ||
        'Something went wrong with terms service';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new TermsService();
